/*
 * One frozen save operation is owned by one verified account and advertiser.
 * The caller supplies PlannerSessionBoundary's scoped storage and current-session
 * fence. This module never accesses an unscoped browser store or a legacy save
 * endpoint. Keep byte-identical in Cortex and Quantum.
 */

export const PLANNER_SAVE_PROTOCOL = "planner-save-receipts-v1";
export const PLANNER_SAVE_RECEIPTS_PATH = "/api/sherloc/media-planner/save-receipts/v1";
export const PLANNER_SAVE_MAX_BYTES = 1024 * 1024;
const PENDING_PROTOCOL = "planner-pending-save-v1";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HASH = /^[0-9a-f]{64}$/;
const VERSION = /^[1-9][0-9]*$/;
const validVersion = (value) => typeof value === "string" && VERSION.test(value) &&
  (value.length < 16 || (value.length === 16 && value <= "9007199254740991"));
const PLAN = /^[A-Za-z0-9_.:-]{1,160}$/;
const TERMINAL = new Set(["COMMITTED", "REJECTED", "CANCELLED"]);

const fault = (code, message) => Object.assign(new Error(message || code), { code });
const cloneJson = (value) => JSON.parse(JSON.stringify(value));
const freeze = (value) => {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};
const ordered = (value) => Array.isArray(value) ? value.map(ordered)
  : value && typeof value === "object"
    ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, ordered(value[key])]))
    : value;
const browserCrypto = () => {
  try { return typeof window === "undefined" ? undefined : window.crypto; }
  catch { return undefined; }
};

// Hash the JSON wire representation, not form fingerprints or a current-row GET.
export const canonicalPlannerSaveRequest = (value) => JSON.stringify(ordered(cloneJson(value)));
export const hashPlannerSaveRequest = async (value, cryptoApi = browserCrypto()) => {
  if (typeof cryptoApi?.subtle?.digest !== "function" || typeof TextEncoder !== "function") {
    throw fault("secure_crypto_unavailable", "Secure save request hashing is unavailable.");
  }
  const bytes = new TextEncoder().encode(canonicalPlannerSaveRequest(value));
  const digest = await cryptoApi.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

// Mirror descriptor-only refusals before allocating any durable operation. A
// refused envelope cannot be settled by the server, so it must never become an
// UNKNOWN pending request. Existing pending operations are never cleared here.
const assertSendableRequest = (request) => {
  const invalid = () => { throw fault("PLANNER_REQUEST_INVALID", "The save was not sent because these inputs have an unsupported structure. Your inputs remain in this browser; correct them before saving again."); };
  const walk = (value, depth = 0) => {
    if (depth > 30) invalid();
    if (value && typeof value === "object") for (const [key, child] of Object.entries(value)) {
      if (["__proto__", "prototype", "constructor"].includes(key)) invalid();
      walk(child, depth + 1);
    }
  };
  walk(request);
  if (!request.savedPayload || typeof request.savedPayload !== "object" || Array.isArray(request.savedPayload)) invalid();
  const source = { ...(request.savedPayload.formData || {}), ...request.savedPayload };
  const positiveId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const advertiser = source.advertiserId ?? source.advertiser_id;
  const target = source.serverDraftId || source.server_draft_id || source.draftCampaignId || source.draft_campaign_id;
  const local = source.localDraftId || source.local_draft_id || source.plannerId || source.planner_id;
  if ((advertiser != null && advertiser !== "" && !positiveId(advertiser)) ||
      (target != null && target !== "" && !positiveId(target)) ||
      (local != null && (typeof local !== "string" || !PLAN.test(local.trim()))) || (!target && !local)) invalid();
  if (typeof TextEncoder !== "function") throw fault("secure_crypto_unavailable");
  if (new TextEncoder().encode(JSON.stringify(request)).byteLength > PLANNER_SAVE_MAX_BYTES) {
    throw fault("PLANNER_REQUEST_TOO_LARGE", "The save was not sent because these inputs exceed the 1 MiB save limit. Your inputs remain in this browser; shorten or copy out some content before saving again.");
  }
};

export const createPlannerSaveRecovery = ({
  storage,
  isCurrent,
  fetch: fetchRequest,
  baseUrl = "",
  headers = () => ({}),
  crypto: cryptoApi = browserCrypto(),
  fetchDeadlineMs = 15000,
  bodyDeadlineMs = 5000,
  now = () => new Date().toISOString(),
} = {}) => {
  const owner = storage?.owner;
  const ownerKey = owner ? JSON.stringify(owner) : "";
  const inFlight = new Map();
  const current = () => {
    try { return Boolean(owner?.portal && owner?.accountId && owner?.advertiserId && isCurrent?.()); }
    catch { return false; }
  };
  const assertCurrent = () => {
    if (!current()) throw fault("session_changed", "Planner account or advertiser access changed.");
  };
  const assertPlan = (planId) => {
    if (!PLAN.test(String(planId || ""))) throw fault("invalid_plan_identity");
  };
  const pendingKey = (planId, requestId) =>
    `sherlocMediaPlanner_${owner.portal}_planner_pending_save_v1_${planId}__${requestId}`;
  const assertRecord = (record, key, planId) => {
    if (record?.protocol !== PENDING_PROTOCOL || record.planId !== planId ||
      JSON.stringify(record.owner) !== ownerKey || !UUID.test(record.request?.requestId || "") ||
      !HASH.test(record.requestHash || "") ||
      record.request?.protocol !== PLANNER_SAVE_PROTOCOL ||
      (record.request.expectedWriteVersion !== null && !validVersion(record.request.expectedWriteVersion)) ||
      Boolean(record.request.savedPayload?.serverDraftId) !== (record.request.expectedWriteVersion !== null) ||
      String(record.request.savedPayload?.advertiserId || "") !== String(owner.advertiserId) ||
      record.request.savedPayload?.localDraftId !== planId ||
      key !== pendingKey(planId, record.request.requestId)) {
      throw fault("invalid_pending_save", "An owned pending save needs reconciliation; no new save was sent.");
    }
    return record;
  };
  const readPending = (planId) => {
    assertCurrent();
    assertPlan(planId);
    if (typeof storage.listPendingSaves !== "function") throw fault("pending_storage_unavailable");
    const entries = storage.listPendingSaves(planId);
    if (!Array.isArray(entries)) throw fault("pending_storage_unavailable");
    return entries.map(({ key, value }) => {
      let parsed;
      try { parsed = JSON.parse(value); } catch { throw fault("invalid_pending_save"); }
      return freeze({ ...assertRecord(parsed, key, planId), key });
    }).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)) || a.request.requestId.localeCompare(b.request.requestId));
  };
  const storeExact = (key, record) => {
    assertCurrent();
    const bytes = JSON.stringify(record);
    storage.setItem(key, bytes);
    assertCurrent();
    if (storage.getItem(key) !== bytes) throw fault("pending_storage_unconfirmed", "The pending save could not be retained safely.");
  };
  const resultFor = (outcome, record, extra = {}) => freeze({
    outcome,
    ...(record ? { request: record.request, requestHash: record.requestHash, planId: record.planId } : {}),
    ...extra,
  });
  const failure = (error, record) => resultFor(
    error?.code === "session_changed" ? "SESSION_CHANGED" : record ? "UNKNOWN" : "BLOCKED",
    record, { code: error?.code || "save_outcome_unknown",
      ...(["PLANNER_REQUEST_TOO_LARGE", "PLANNER_REQUEST_INVALID"].includes(error?.code) ? { message: error.message } : {}) }
  );
  const withDeadline = (promise, milliseconds, controller) => new Promise((resolve, reject) => {
    let complete = false;
    const timer = setTimeout(() => {
      if (complete) return;
      complete = true;
      // Aborting the browser wait is never evidence that the server cancelled.
      controller?.abort();
      reject(fault("save_deadline_exceeded"));
    }, milliseconds);
    Promise.resolve(promise).then((value) => {
      if (complete) return;
      complete = true;
      clearTimeout(timer);
      resolve(value);
    }, (error) => {
      if (complete) return;
      complete = true;
      clearTimeout(timer);
      reject(error);
    });
  });
  const call = async (method, path, body) => {
    assertCurrent();
    if (typeof fetchRequest !== "function") throw fault("transport_unavailable");
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const requestHeaders = typeof headers === "function" ? headers() : headers;
    assertCurrent();
    const response = await withDeadline(fetchRequest(`${String(baseUrl).replace(/\/$/, "")}${path}`, {
      method,
      headers: { ...requestHeaders, "Content-Type": "application/json" },
      cache: "no-store",
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    }), fetchDeadlineMs, controller);
    assertCurrent();
    if (!response || typeof response.json !== "function") throw fault("invalid_save_response");
    const data = await withDeadline(response.json(), bodyDeadlineMs, controller);
    assertCurrent();
    return { data, status: response.status };
  };
  const validateReceipt = (record, data) => {
    const receipt = data?.receipt;
    if (data?.protocol !== PLANNER_SAVE_PROTOCOL || !receipt || !TERMINAL.has(receipt.outcome) ||
      receipt.requestId !== record.request.requestId || receipt.requestHash !== record.requestHash ||
      String(receipt.advertiserId || "") !== String(owner.advertiserId) ||
      receipt.localDraftId !== record.planId ||
      (receipt.immutableRevisionId !== null && receipt.immutableRevisionId !== undefined)) {
      throw fault("invalid_save_receipt");
    }
    if (data.draft && (!validVersion(String(data.draft.serverDraftId ?? data.draft.id ?? "")) ||
      (data.draft.id !== undefined && data.draft.serverDraftId !== undefined &&
        String(data.draft.id) !== String(data.draft.serverDraftId)) ||
      String(data.draft.advertiserId ?? "") !== String(owner.advertiserId) ||
      !validVersion(data.draft.writeVersion))) throw fault("invalid_save_receipt");
    if (receipt.outcome === "COMMITTED") {
      if (!/^[1-9][0-9]*$/.test(String(receipt.serverDraftId || "")) ||
        !validVersion(receipt.writeVersion) ||
        !HASH.test(receipt.snapshotHash || "") ||
        (record.request.savedPayload.serverDraftId &&
          String(receipt.serverDraftId) !== String(record.request.savedPayload.serverDraftId))) {
        throw fault("invalid_save_receipt");
      }
      if (record.request.savedPayload.writeKind === "GOVERNED_DRAFT_CAMPAIGN_UPDATE" &&
        (!data.campaign || String(data.campaign.id ?? data.campaign.campaign_id ?? "") !== String(receipt.serverDraftId) ||
          (data.campaign.advertiser_id !== undefined && String(data.campaign.advertiser_id) !== String(owner.advertiserId)) ||
          (data.campaign.advertiserId !== undefined && String(data.campaign.advertiserId) !== String(owner.advertiserId)))) {
        throw fault("invalid_save_receipt");
      }
      if (data.draft && String(data.draft.serverDraftId ?? data.draft.id ?? "") !== String(receipt.serverDraftId)) {
        throw fault("invalid_save_receipt");
      }
    }
    return receipt;
  };
  const acceptTerminal = async (record, data) => {
    assertCurrent();
    if (await hashPlannerSaveRequest(record.request, cryptoApi) !== record.requestHash) throw fault("pending_request_changed");
    assertCurrent();
    const receipt = validateReceipt(record, data);
    // Retain the terminal fact until the page acknowledges its local binding.
    // A crash between this return and a page update is recoverable on refresh.
    const key = pendingKey(record.planId, record.request.requestId);
    const stored = readPending(record.planId).find((item) => item.key === key);
    if (!stored || stored.requestHash !== record.requestHash) throw fault("pending_record_changed");
    const withoutKey = { ...stored };
    delete withoutKey.key;
    storeExact(key, { ...withoutKey, terminal: cloneJson(data) });
    return resultFor(receipt.outcome, record, {
      receipt: cloneJson(receipt), draft: data.draft ? cloneJson(data.draft) : null,
      responseBody: cloneJson(data),
      superseded: data.superseded === true ||
        (receipt.outcome === "COMMITTED" && (!data.draft || data.draft.writeVersion !== receipt.writeVersion)),
    });
  };
  const recoverRecord = async (record) => {
    try {
      assertCurrent();
      if (await hashPlannerSaveRequest(record.request, cryptoApi) !== record.requestHash) throw fault("pending_request_changed");
      assertCurrent();
      // Even a retained terminal fact must refresh the current projection before
      // it can describe what is saved now. The historical receipt remains true;
      // a cached draft does not establish that no other writer changed the row.
      const statusPath = `${PLANNER_SAVE_RECEIPTS_PATH}/requests/${encodeURIComponent(record.request.requestId)}?advertiserId=${encodeURIComponent(owner.advertiserId)}`;
      const status = await call("GET", statusPath);
      if (TERMINAL.has(status.data?.receipt?.outcome)) return await acceptTerminal(record, status.data);
      // Only a protocol UNKNOWN response permits settlement. A failed/malformed
      // status request is not absence and does not clear the pending operation.
      if (status.data?.protocol !== PLANNER_SAVE_PROTOCOL || status.data?.receipt?.outcome !== "UNKNOWN" ||
        status.data.receipt.requestId !== record.request.requestId ||
        (status.data.receipt.advertiserId !== null &&
          String(status.data.receipt.advertiserId) !== String(owner.advertiserId)) ||
        status.status !== 200) throw fault("save_status_unavailable");
      const settled = await call("POST", `${PLANNER_SAVE_RECEIPTS_PATH}/requests/${encodeURIComponent(record.request.requestId)}/settle`, record.request);
      return await acceptTerminal(record, settled.data);
    } catch (error) { return failure(error, record); }
  };
  const exclusive = (planId, operation) => {
    // A different click may carry different content. Never give it the first
    // operation's successful result as if that result acknowledged its input.
    if (inFlight.has(planId)) return Promise.resolve(resultFor("BUSY", null, { planId }));
    const promise = Promise.resolve().then(operation).finally(() => {
      if (inFlight.get(planId) === promise) inFlight.delete(planId);
    });
    inFlight.set(planId, promise);
    return promise;
  };
  const run = (input = {}) => {
    let copiedInput;
    try { copiedInput = cloneJson(input); }
    catch (error) { return Promise.resolve(failure(fault("invalid_save_payload"))); }
    const { planId, savedPayload, expectedWriteVersion = null } = copiedInput;
    return exclusive(planId, async () => {
    let record;
    let dispatched = false;
    try {
      assertCurrent();
      const existing = readPending(planId);
      if (existing.length) return resultFor("RECOVERY_REQUIRED", existing[0]);
      if (typeof cryptoApi?.randomUUID !== "function") throw fault("secure_crypto_unavailable");
      const requestId = cryptoApi.randomUUID();
      const request = cloneJson({ protocol: PLANNER_SAVE_PROTOCOL, requestId, expectedWriteVersion, savedPayload });
      assertSendableRequest(request);
      freeze(request);
      const requestHash = await hashPlannerSaveRequest(request, cryptoApi);
      assertCurrent();
      record = { protocol: PENDING_PROTOCOL, owner: cloneJson(owner), planId, request, requestHash, createdAt: now() };
      const key = pendingKey(planId, requestId);
      assertRecord(record, key, planId);
      // Hashing yields to other tabs/callers; inspect again before allocating.
      const pending = readPending(planId);
      if (pending.length) return resultFor("RECOVERY_REQUIRED", pending[0]);
      storeExact(key, record);
      dispatched = true;
      const response = await call("POST", PLANNER_SAVE_RECEIPTS_PATH, request);
      return await acceptTerminal(record, response.data);
    } catch (error) { return failure(error, dispatched ? record : undefined); }
    });
  };
  const recover = (planId) => exclusive(planId, async () => {
    try {
      const records = readPending(planId);
      const results = [];
      for (const record of records) {
        const result = await recoverRecord(record);
        results.push(result);
        if (result.outcome === "SESSION_CHANGED") break;
      }
      return freeze({ outcome: results.some((result) => !TERMINAL.has(result.outcome)) ? "UNKNOWN" : "RECOVERED", results });
    } catch (error) { return failure(error); }
  });
  const acknowledge = (planId, requestId, requestHash) => {
    assertCurrent();
    const record = readPending(planId).find((item) => item.request.requestId === requestId);
    if (!record || record.requestHash !== requestHash || !record.terminal) throw fault("terminal_receipt_required");
    validateReceipt(record, record.terminal);
    assertCurrent();
    storage.removeItem(record.key);
    assertCurrent();
    if (storage.getItem(record.key) !== null) throw fault("pending_storage_unconfirmed");
  };
  const readDraft = async (serverDraftId) => {
    try {
      assertCurrent();
      const id = String(serverDraftId ?? "");
      if (!validVersion(id)) throw fault("invalid_draft_identity");
      const { data, status } = await call("GET",
        `${PLANNER_SAVE_RECEIPTS_PATH}/drafts/${encodeURIComponent(id)}?advertiserId=${encodeURIComponent(owner.advertiserId)}`);
      const draft = data?.draft;
      if (status !== 200 || data?.ok !== true || data?.protocol !== PLANNER_SAVE_PROTOCOL ||
        !draft || String(draft.serverDraftId ?? draft.id ?? "") !== id ||
        (draft.id !== undefined && String(draft.id) !== id) ||
        String(draft.advertiserId ?? "") !== String(owner.advertiserId) ||
        !validVersion(draft.writeVersion)) throw fault("invalid_draft_response");
      assertCurrent();
      // This projection supports explicit input reconciliation only. A row
      // read never establishes a save outcome or changes pending receipts.
      return freeze({ outcome: "READ", draft: cloneJson(draft) });
    } catch (error) { return failure(error); }
  };
  return Object.freeze({ run, recover, readPending, acknowledge, readDraft });
};
